import {
  type Chain,
  type ContractFunctionExecutionErrorType,
  erc20Abi,
  erc721Abi,
  EstimateGasExecutionError,
  getContract,
  InsufficientFundsError,
  isAddress,
  type LocalAccount,
  type PublicClient,
  type Transport,
  type WalletClient,
} from "viem";

import { IKeybanApi } from "~/api";
import { SdkError } from "~/errors";
import {
  type Address,
  EstimateERC20TransferParams,
  EstimateNftTransferParams,
  FeesEstimation,
  type Hash,
  type Hex,
  KeybanAccount,
  TransferERC20Params,
  TransferNftParams,
} from "~/index";

const ERC1155_ABI_TRANSFER_FROM = [
  {
    type: "function",
    name: "safeTransferFrom",
    inputs: [
      { name: "from", type: "address", internalType: "address" },
      { name: "to", type: "address", internalType: "address" },
      { name: "id", type: "uint256", internalType: "uint256" },
      { name: "value", type: "uint256", internalType: "uint256" },
      { name: "data", type: "bytes", internalType: "bytes" },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
];

/**
 * EVM-specific {@link KeybanAccount} implementation using viem.
 * Supports native transfers, ERC‑20, and NFTs (ERC‑721 / ERC‑1155) with EIP‑1559 fees.
 * Obtain instances via {@link KeybanClient.initialize} for an EVM network.
 * @see {@link KeybanAccount}
 * @see {@link EvmFeeDetails}
 */
export class KeybanEvmAccount extends KeybanAccount {
  #publicClient: PublicClient<Transport, Chain>;
  #walletClient: WalletClient<Transport, Chain, LocalAccount>;

  constructor(
    api: IKeybanApi,
    publicClient: PublicClient<Transport, Chain>,
    walletClient: WalletClient<Transport, Chain, LocalAccount>,
  ) {
    super(api);

    this.#publicClient = publicClient;
    this.#walletClient = walletClient;
  }

  get address(): Address {
    return this.#walletClient.account.address.toLowerCase() as Address;
  }

  get publicKey(): Hex {
    return this.#walletClient.account.publicKey.toLowerCase() as Hex;
  }

  /**
   * Sign an arbitrary message with the EVM account.
   * @param message - The message to sign.
   * @returns The signature as a hex string.
   * @example
   * const sig = await account.signMessage("hello");
   * console.log(sig);
   */
  async signMessage(message: string): Promise<Hex> {
    // For now, even EDDSA messages are prefixed with the Ethereum message prefix.
    // This may need to be updated when the eddsa signer is associated with a specific chain (e.g., Solana).
    return this.#walletClient.signMessage({ message });
  }

  /**
   * Transfer native currency on an EVM chain (e.g., ETH/MATIC) using EIP‑1559.
   * @param to - Recipient address.
   * @param value - Amount in wei.
   * @param [feeDetails] - Optional EIP‑1559 fee overrides; see {@link EvmFeeDetails}.
   * @returns Transaction hash.
   * @throws {SdkError} {@link SdkError.types.AddressInvalid} | {@link SdkError.types.AmountInvalid} | {@link SdkError.types.InsufficientFunds}
   * @example
   * const tx = await account.transfer("0x0000000000000000000000000000000000000001", 1_000_000_000_000_000n);
   * console.log(tx);
   */
  async transfer(
    to: Address,
    value: bigint,
    feeDetails?: EvmFeeDetails,
  ): Promise<Hash> {
    if (!isAddress(to)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transfer",
      );
    }

    if (value <= 0n) {
      throw new SdkError(
        SdkError.types.AmountInvalid,
        "KeybanAccount.transfer",
      );
    }

    return this.#walletClient
      .sendTransaction({
        to,
        value,
        type: "eip1559",
        ...feeDetails,
      })
      .catch((err) => {
        throw err.cause;
      });
  }

  /**
   * Estimate EIP‑1559 fees for a native transfer.
   * @param to - Recipient address.
   * @returns An estimation with {@link EvmFeeDetails} under `details`.
   */
  async estimateTransfer(to: Address): Promise<FeesEstimation> {
    const [{ maxFeePerGas, maxPriorityFeePerGas }, gasCost] = await Promise.all(
      [
        this.#publicClient.estimateFeesPerGas({ type: "eip1559" }),
        this.#publicClient.estimateGas({
          to: to as `0x${string}`,
          account: this.address as `0x${string}`,
        }),
      ],
    );

    return {
      maxFees: maxFeePerGas * gasCost,
      details: {
        maxFeePerGas,
        maxPriorityFeePerGas,
      },
    };
  }

  /**
   * Transfer ERC‑20 tokens.
   * @param params - Transfer parameters including `contractAddress`, `to`, `value`, and optional `fees`.
   * @returns Transaction hash.
   * @throws {SdkError} {@link SdkError.types.AddressInvalid} | {@link SdkError.types.AmountInvalid} | {@link SdkError.types.RecipientAddressEqualsSender} | {@link SdkError.types.InsufficientFunds}
   * @example
   * const tx = await account.transferERC20({ contractAddress, to, value: 1_000_000n });
   * console.log(tx);
   */
  async transferERC20(
    params: TransferERC20Params<EvmFeeDetails>,
  ): Promise<Hash> {
    const { contractAddress, to, value, fees } = params;

    if (!isAddress(to)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transferERC20",
      );
    }
    if (!isAddress(contractAddress)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transferERC20",
      );
    }
    if (to === this.address) {
      throw new SdkError(
        SdkError.types.RecipientAddressEqualsSender,
        "KeybanAccount.transferERC20",
      );
    }

    if (value <= 0n) {
      throw new SdkError(
        SdkError.types.AmountInvalid,
        "KeybanAccount.transferERC20",
      );
    }

    const erc20Contract = getContract({
      address: contractAddress,
      abi: erc20Abi,
      client: {
        public: this.#publicClient,
        wallet: this.#walletClient,
      },
    });

    return erc20Contract.write
      .transfer([to, value], fees)
      .catch((err: ContractFunctionExecutionErrorType) => {
        switch (true) {
          case err.cause.cause instanceof InsufficientFundsError:
          case err.cause.cause instanceof EstimateGasExecutionError:
            throw new SdkError(
              SdkError.types.InsufficientFunds,
              "KeybanAccount.transferERC20",
            );

          default:
            throw err.cause;
        }
      });
  }

  /**
   * Estimate EIP‑1559 fees for an ERC‑20 `transfer`.
   * @param params - Estimation parameters without explicit fees.
   * @returns An estimation with {@link EvmFeeDetails} under `details`.
   */
  async estimateERC20Transfer(
    params: EstimateERC20TransferParams,
  ): Promise<FeesEstimation> {
    const { contractAddress, to, value } = params;
    const [{ maxFeePerGas, maxPriorityFeePerGas }, gasCost] = await Promise.all(
      [
        this.#publicClient.estimateFeesPerGas({ type: "eip1559" }),
        this.#publicClient.estimateContractGas({
          address: contractAddress as `0x${string}`,
          abi: erc20Abi,
          functionName: "transfer",
          args: [to as `0x${string}`, value],
          account: this.address as `0x${string}`,
        }),
      ],
    );

    return {
      maxFees: maxFeePerGas * gasCost,
      details: {
        maxFeePerGas,
        maxPriorityFeePerGas,
      },
    };
  }

  /**
   * Transfer an NFT (ERC‑721 / ERC‑1155).
   * @param params - NFT transfer parameters. For ERC‑1155, `value` must be provided and > 0. For ERC‑721, `value` is ignored or 1.
   * @returns Transaction hash.
   * @throws {SdkError} {@link SdkError.types.AddressInvalid} | {@link SdkError.types.RecipientAddressEqualsSender} | {@link SdkError.types.AmountRequired} | {@link SdkError.types.AmountInvalid} | {@link SdkError.types.AmountIrrelevant} | {@link SdkError.types.InvalidNftStandard} | {@link SdkError.types.InsufficientFunds}
   * @example
   * await account.transferNft({ contractAddress, tokenId: 1n, to, standard: "ERC721" });
   */
  async transferNft(params: TransferNftParams<EvmFeeDetails>): Promise<Hash> {
    const { contractAddress, tokenId, to, value, standard, fees } = params;

    if (!isAddress(to)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transferNft",
      );
    }
    if (!isAddress(contractAddress)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transferNft",
      );
    }
    if (to === this.address) {
      throw new SdkError(
        SdkError.types.RecipientAddressEqualsSender,
        "KeybanAccount.transferNft",
      );
    }

    if (standard === "ERC1155") {
      if (value === undefined) {
        throw new SdkError(
          SdkError.types.AmountRequired,
          "KeybanAccount.transferNft",
        );
      }
      if (value <= 0n) {
        throw new SdkError(
          SdkError.types.AmountInvalid,
          "KeybanAccount.transferNft",
        );
      }
      return this.#transferERC1155({
        contractAddress,
        tokenId,
        value,
        to,
        fees,
      });
    }

    if (standard === "ERC721") {
      if (value !== undefined && value !== 1n) {
        throw new SdkError(
          SdkError.types.AmountIrrelevant,
          "KeybanAccount.transferNft",
        );
      }
      return this.#transferERC721({ contractAddress, tokenId, to, fees });
    }

    throw new SdkError(
      SdkError.types.InvalidNftStandard,
      "KeybanAccount.transferNft",
    );
  }

  async #transferERC721({
    contractAddress,
    tokenId,
    to,
    fees,
  }: Omit<
    TransferNftParams<EvmFeeDetails>,
    "value" | "standard"
  >): Promise<Hash> {
    const erc721Contract = getContract({
      address: contractAddress as `0x${string}`,
      abi: erc721Abi,
      client: {
        public: this.#publicClient,
        wallet: this.#walletClient,
      },
    });

    const from = this.address;
    return erc721Contract.write
      .transferFrom([from as `0x${string}`, to as `0x${string}`, tokenId], fees)
      .catch((err: ContractFunctionExecutionErrorType) => {
        switch (true) {
          case err.cause.cause instanceof InsufficientFundsError:
          case err.cause.cause instanceof EstimateGasExecutionError:
            throw new SdkError(
              SdkError.types.InsufficientFunds,
              "KeybanAccount.transferNft",
            );

          default:
            throw err.cause;
        }
      });
  }

  async #transferERC1155({
    contractAddress,
    tokenId,
    value,
    to,
    fees,
  }: Omit<TransferNftParams, "standard">): Promise<Hash> {
    const erc1155Contract = getContract({
      address: contractAddress as `0x${string}`,
      abi: ERC1155_ABI_TRANSFER_FROM,
      client: {
        public: this.#publicClient,
        wallet: this.#walletClient,
      },
    });

    const from = this.address;
    return erc1155Contract.write
      .safeTransferFrom([from, to, tokenId, value, ""], fees)
      .catch((err: ContractFunctionExecutionErrorType) => {
        switch (true) {
          case err.cause.cause instanceof InsufficientFundsError:
          case err.cause.cause instanceof EstimateGasExecutionError:
            throw new SdkError(
              SdkError.types.InsufficientFunds,
              "KeybanAccount.transferNft",
            );

          default:
            throw err.cause;
        }
      });
  }

  /**
   * Estimate EIP‑1559 fees for an NFT transfer.
   * @param params - Estimation parameters.
   * @returns An estimation with {@link EvmFeeDetails} under `details`.
   */
  async estimateNftTransfer(
    params: EstimateNftTransferParams,
  ): Promise<FeesEstimation> {
    const { standard, contractAddress, tokenId, to, value } = params;

    if (standard === "ERC1155") {
      return this.#estimateERC1155Transfer({
        contractAddress,
        tokenId,
        to,
        value,
      });
    }
    if (standard === "ERC721") {
      if (value !== undefined && value !== 1n) {
        throw new SdkError(
          SdkError.types.AmountIrrelevant,
          "KeybanAccount.transferNft",
        );
      }
      return this.#estimateERC721Transfer({ contractAddress, tokenId, to });
    }
    throw new SdkError(
      SdkError.types.InvalidNftStandard,
      "KeybanAccount.estimateNftTransfer",
    );
  }

  async #estimateERC721Transfer({
    contractAddress,
    tokenId,
    to,
  }: Omit<
    EstimateNftTransferParams,
    "standard" | "value"
  >): Promise<FeesEstimation> {
    const from = this.address;
    const [{ maxFeePerGas, maxPriorityFeePerGas }, gasCost] = await Promise.all(
      [
        this.#publicClient.estimateFeesPerGas({ type: "eip1559" }),
        this.#publicClient.estimateContractGas({
          address: contractAddress as `0x${string}`,
          abi: erc721Abi,
          functionName: "transferFrom",
          args: [from as `0x${string}`, to as `0x${string}`, tokenId],
          account: this.address as `0x${string}`,
        }),
      ],
    );

    return {
      maxFees: maxFeePerGas * gasCost,
      details: {
        maxFeePerGas,
        maxPriorityFeePerGas,
      },
    };
  }

  async #estimateERC1155Transfer({
    contractAddress,
    tokenId,
    to,
    value,
  }: Omit<EstimateNftTransferParams, "standard">): Promise<FeesEstimation> {
    const from = this.address;
    const [{ maxFeePerGas, maxPriorityFeePerGas }, gasCost] = await Promise.all(
      [
        this.#publicClient.estimateFeesPerGas({ type: "eip1559" }),
        this.#publicClient.estimateContractGas({
          address: contractAddress as `0x${string}`,
          abi: ERC1155_ABI_TRANSFER_FROM,
          functionName: "safeTransferFrom",
          args: [from, to, tokenId, value, ""],
          account: this.address as `0x${string}`,
        }),
      ],
    );

    return {
      maxFees: maxFeePerGas * gasCost,
      details: {
        maxFeePerGas,
        maxPriorityFeePerGas,
      },
    };
  }
}

/**
 * EIP‑1559 fee parameters for EVM transactions.
 * Used by EVM account transfer methods to override gas pricing when needed.
 * @example
 * const fees: EvmFeeDetails = { maxFeePerGas: 30n * 10n ** 9n, maxPriorityFeePerGas: 2n * 10n ** 9n };
 */
export type EvmFeeDetails = {
  /** The maximum fee per unit of gas. */
  maxFeePerGas: bigint;
  /** The maximum priority fee per unit of gas. */
  maxPriorityFeePerGas: bigint;
};
